import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { Scale, RefreshCw, Search, FileDown, Store, TrendingDown, TrendingUp, Package, Ruler } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { useAuth } from '../context/AuthContext';
import CompetitorMissingProducts from './CompetitorMissingProducts';

const API_BASE = 'https://api.glowytics.com';

const fmt = (v) =>
  v == null ? '—' : `₺${Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const fmtNum = (n) => Number(n || 0).toLocaleString('en-US');

// Chart / badge colors — same green / gray / red used across the page
const POS_COLORS = { cheaper: '#16a34a', equal: '#9ca3af', pricier: '#dc2626' };

export default function CompetitorOnComparison() {
  const { selectedBrand } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [brand, setBrand] = useState('');           // '' = all
  const [onlyMatched, setOnlyMatched] = useState(true);
  const [seller, setSeller] = useState('');         // '' = all sellers (compare vs market min)
  const [position, setPosition] = useState('');     // '' | 'pricier' | 'cheaper' | 'same'
  const [pdfExporting, setPdfExporting] = useState(false);
  const [tab, setTab] = useState('price');          // 'price' | 'missing' (only when the API has a gap table)

  // Off-screen, fully styled report ref captured by html2canvas for the PDF
  const reportRef = useRef(null);

  // Site lists come from the API (High5 / Wunder etc.) — first site is "us", the rest are competitors.
  const OUR_SITE = data?.ourSite || 'High5';
  const COMP_SITES = useMemo(
    () => data?.compSites || data?.sites?.slice(1) || ['Vakkorama', 'Superstep', 'SPX'],
    [data]
  );
  const ALL_SITES = useMemo(() => [OUR_SITE, ...COMP_SITES], [OUR_SITE, COMP_SITES]);
  const SITE_LABEL = useMemo(() => {
    const m = { [OUR_SITE]: data?.ourLabel || `${OUR_SITE} (Us)` };
    COMP_SITES.forEach((s) => { m[s] = s; });
    return m;
  }, [OUR_SITE, COMP_SITES, data]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/competitor-intel/comparison`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': selectedBrand?.id || localStorage.getItem('currentBrandId') || '' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (err) {
      setError(err.message);
      toast.error('Could not load comparison. ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedBrand?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const rows = useMemo(() => {
    let r = data?.rows || [];
    if (brand) r = r.filter((x) => (x.brand || '') === brand);
    if (onlyMatched) r = r.filter((x) => x.matchCount > 0);
    // Seller filter: only products this seller also carries.
    if (seller) r = r.filter((x) => x.prices?.[seller] != null);
    // Price position. With a seller selected we compare against THAT seller; with
    // "all sellers" against the market minimum (the cheapest rival on that product).
    if (position) {
      r = r.filter((x) => {
        const ours = x.prices?.[OUR_SITE];
        const rival = seller ? x.prices?.[seller] : x.marketMin;
        if (ours == null || rival == null) return false;
        if (position === 'pricier') return ours > rival;
        if (position === 'cheaper') return ours < rival;
        return ours === rival;
      });
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      r = r.filter(
        (x) =>
          (x.name || '').toLowerCase().includes(q) ||
          (x.productId || '').toLowerCase().includes(q) ||
          (x.brand || '').toLowerCase().includes(q)
      );
    }
    // matched first, then the ones where we are most expensive on top (action priority)
    return [...r].sort((a, b) => (b.matchCount - a.matchCount) || ((b.priceIndex || 0) - (a.priceIndex || 0)));
  }, [data, brand, onlyMatched, search, seller, position, OUR_SITE]);

  // Counts for the price-position buttons. Same scope as the detail table but WITHOUT the
  // position filter itself, so each button shows how many rows it would leave.
  const positionCounts = useMemo(() => {
    let r = (data?.rows || []).filter((x) => x.matchCount > 0);
    if (brand) r = r.filter((x) => (x.brand || '') === brand);
    if (seller) r = r.filter((x) => x.prices?.[seller] != null);
    const c = { all: r.length, pricier: 0, cheaper: 0, same: 0 };
    for (const x of r) {
      const ours = x.prices?.[OUR_SITE];
      const rival = seller ? x.prices?.[seller] : x.marketMin;
      if (ours == null || rival == null) continue;
      if (ours > rival) c.pricier += 1;
      else if (ours < rival) c.cheaper += 1;
      else c.same += 1;
    }
    return c;
  }, [data, brand, seller, OUR_SITE]);

  // Per-seller summary. Computed from the product rows (NOT the brand aggregates) because
  // per-seller price position and match-method mix only exist at row level. Scope follows the
  // brand filter but deliberately ignores the search box — this is a market view, not a result set.
  const sellerRows = useMemo(() => {
    const base = (data?.rows || []).filter((x) => !brand || (x.brand || '') === brand);
    return COMP_SITES.map((site) => {
      const acc = { site, matched: 0, weCheaper: 0, wePricier: 0, same: 0,
        cheapest: 0, gapSum: 0, gapN: 0, methods: {} };
      for (const r of base) {
        const theirs = r.prices?.[site];
        const ours = r.prices?.[OUR_SITE];
        if (theirs == null) continue;
        acc.matched += 1;
        const m = r.methods?.[site];
        if (m) acc.methods[m] = (acc.methods[m] || 0) + 1;
        if (r.cheapestSite === site) acc.cheapest += 1;
        if (ours == null) continue;
        if (ours < theirs) acc.weCheaper += 1;
        else if (ours > theirs) acc.wePricier += 1;
        else acc.same += 1;
        if (theirs > 0) { acc.gapSum += ((ours - theirs) / theirs) * 100; acc.gapN += 1; }
      }
      acc.avgGap = acc.gapN ? acc.gapSum / acc.gapN : null;
      return acc;
    }).sort((a, b) => b.matched - a.matched);
  }, [data, brand, COMP_SITES, OUR_SITE]);

  // Product overlap per competitor: shared / only us / only them. "Only them" comes from the
  // gap table (limited to our brands + product groups), so it is not the mirror of "only us".
  const overlap = useMemo(() => {
    if (!data?.hasMissing) return null;
    const all = data.rows || [];
    const cards = COMP_SITES.map((s) => {
      const shared = all.filter((r) => r.prices?.[s] != null).length;
      return { site: s, shared, onlyUs: all.length - shared,
        onlyThem: data.missingBySite?.[s]?.total || 0, onlyThemStock: data.missingBySite?.[s]?.inStock || 0 };
    });
    const sharedAny = all.filter((r) => r.matchCount > 0).length;
    cards.push({ site: 'All competitors', all: true, shared: sharedAny, onlyUs: all.length - sharedAny,
      onlyThem: data.missingBySite?._all?.total || 0, onlyThemStock: data.missingBySite?._all?.inStock || 0 });
    return { total: all.length, cards };
  }, [data, COMP_SITES]);

  const overlapXlsxUrl = `${API_BASE}/api/competitor-intel/overlap.xlsx?brandId=${
    selectedBrand?.id || localStorage.getItem('currentBrandId') || ''}`;

  // scope summary by selected brand (all brands combined when none selected)
  const scopeBrands = useMemo(() => {
    const bs = data?.brands || [];
    return brand ? bs.filter((b) => b.brand === brand) : bs;
  }, [data, brand]);

  const summary = useMemo(() => {
    const s = { total: 0, matched: 0, weCheapest: 0, wePricier: 0,
      cheapestBy: Object.fromEntries(ALL_SITES.map((k) => [k, 0])),
      siteCounts: Object.fromEntries(COMP_SITES.map((k) => [k, 0])) };
    for (const b of scopeBrands) {
      s.total += b.total; s.matched += b.matched;
      s.weCheapest += b.weCheapest; s.wePricier += b.wePricier;
      for (const k of ALL_SITES) s.cheapestBy[k] += (b.cheapestBy?.[k] || 0);
      for (const k of COMP_SITES) s.siteCounts[k] += (b.siteCounts?.[k] || 0);
    }
    return s;
  }, [scopeBrands, ALL_SITES, COMP_SITES]);

  // Our own size × stock health across the matched products (same scope as the stat cards).
  // Source is the hebiar SKU feed, NOT the catalog snapshot — see `ourSizeDate` in the header.
  const ourSizeSummary = useMemo(() => {
    let withData = 0, total = 0, avail = 0, fullyOut = 0, critical = 0;
    for (const r of data?.rows || []) {
      if (brand && (r.brand || '') !== brand) continue;
      if (!(r.matchCount > 0)) continue;
      const sz = r.sizes?.[OUR_SITE];
      if (!sz || sz.total == null) continue;
      withData++; total += sz.total; avail += sz.avail || 0;
      if (!sz.avail) fullyOut++;
      else if (sz.avail / sz.total <= 0.25) critical++;
    }
    return { withData, total, avail, fullyOut, critical,
      pct: total ? Math.round((avail / total) * 100) : null };
  }, [data, brand, OUR_SITE]);

  // For PDF/charts: brands with at least one match, sorted by matches (same as the table)
  const brandRows = useMemo(
    () => (data?.brands || []).filter((b) => b.matched > 0).sort((a, b) => b.matched - a.matched),
    [data]
  );

  // Overall price position (donut): cheaper / equal-uncertain / pricier
  const posData = useMemo(() => {
    const equal = Math.max(0, summary.matched - summary.weCheapest - summary.wePricier);
    return [
      { name: 'Cheaper here', value: summary.weCheapest, color: POS_COLORS.cheaper },
      { name: 'Equal / uncertain', value: equal, color: POS_COLORS.equal },
      { name: 'Pricier here', value: summary.wePricier, color: POS_COLORS.pricier },
    ];
  }, [summary]);
  const posTotal = posData.reduce((a, b) => a + b.value, 0);

  // Cheaper vs pricier per brand (stacked bar)
  const barData = useMemo(
    () => brandRows.map((b) => ({ brand: b.brand, cheaper: b.weCheapest, pricier: b.wePricier })),
    [brandRows]
  );

  const topCheapestSeller = useMemo(() => Object.entries(summary.cheapestBy)
    .filter(([k]) => k !== OUR_SITE)
    .sort((a, b) => b[1] - a[1])[0], [summary, OUR_SITE]);

  const exportPDF = useCallback(async () => {
    if (pdfExporting || !reportRef.current) return;
    setPdfExporting(true);
    try {
      // Capture the whole report at once — text is rendered by the browser
      const canvas = await html2canvas(reportRef.current, { scale: 2, backgroundColor: '#ffffff', logging: false });
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const imgW = pageW - margin * 2;
      const pxPerMm = canvas.width / imgW;            // horizontal scale
      const pageContentPx = (pageH - margin * 2) * pxPerMm; // pixel height that fits one page

      let rendered = 0;
      let page = 0;
      while (rendered < canvas.height) {
        const sliceH = Math.min(pageContentPx, canvas.height - rendered);
        const slice = document.createElement('canvas');
        slice.width = canvas.width;
        slice.height = sliceH;
        const ctx = slice.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, slice.width, slice.height);
        ctx.drawImage(canvas, 0, rendered, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
        if (page > 0) pdf.addPage();
        pdf.addImage(slice.toDataURL('image/png'), 'PNG', margin, margin, imgW, sliceH / pxPerMm);
        rendered += sliceH;
        page += 1;
      }

      pdf.save(`price-comparison-${OUR_SITE.toLowerCase()}-${brand || 'all-brands'}.pdf`);
    } catch (e) {
      toast.error('Could not generate PDF: ' + (e?.message || e));
    } finally {
      setPdfExporting(false);
    }
  }, [pdfExporting, brand, OUR_SITE]);

  if (loading) {
    return (
      <div className="p-6 max-w-[1320px] mx-auto">
        <Card className="rounded-2xl border border-gray-100 shadow-sm p-10 flex items-center justify-center text-gray-500">
          <RefreshCw className="w-5 h-5 mr-2 animate-spin" /> Loading…
        </Card>
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6 max-w-[1320px] mx-auto">
        <Card className="rounded-2xl border border-red-100 shadow-sm p-8 text-red-600">
          Error: {error}
          <button onClick={fetchData} className="ml-3 underline">Try again</button>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-[1320px] mx-auto">
      {/* Header + KPI cards */}
      <Card className="rounded-2xl overflow-hidden shadow-xl border border-gray-100">
        <div className="p-6 bg-white border-b">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center">
                <Scale className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Price Comparison</h1>
                <p className="text-sm text-gray-500 mt-1 max-w-3xl">
                  Price benchmark of {OUR_SITE} products against market sellers ({COMP_SITES.join(', ')}).
                  Matching is done by barcode (GTIN) first, then style code / brand + model + color, then
                  brand + model + gender; updated daily. The badge under each price shows which method matched —
                  <span className="text-amber-600"> model+gender</span> is model-level, not colour-level.
                  {data?.minCompSizes && ` A seller only counts in cheaper / pricier when it can actually sell the item: at least ${data.minCompSizes} sizes in stock, or in stock at the last crawl where sizes are not available. Excluded prices are struck through.`}
                  {selectedBrand?.name ? ` · ${selectedBrand.name}` : ''}
                  {data?.dataDates && (
                    <span className="ml-1 text-gray-400">
                      · Data: {Object.entries(data.dataDates).map(([s, d]) => `${s} ${d}`).join(' · ')}
                      {data?.ourSizeDate && ` · Our sizes: ${data.ourSizeDate} (live SKU feed)`}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={exportPDF}
                disabled={pdfExporting || brandRows.length === 0}
                className="inline-flex items-center gap-1.5 px-3.5 h-10 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FileDown className="w-4 h-4" />
                {pdfExporting ? 'Preparing…' : 'Download PDF'}
              </button>
              <button
                type="button"
                onClick={fetchData}
                disabled={loading}
                className="flex items-center justify-center w-10 h-10 bg-white border rounded-xl text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                title="Refresh"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Summary stat cards */}
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <StatCard icon={Package} color="text-indigo-600 bg-indigo-50"
              label="Matched products" value={fmtNum(summary.matched)} sub={`${fmtNum(summary.total)} ${OUR_SITE} products`} />
            <StatCard icon={TrendingDown} color="text-emerald-600 bg-emerald-50"
              label="Cheaper here" value={fmtNum(summary.weCheapest)} valueColor="text-emerald-600" />
            <StatCard icon={TrendingUp} color="text-red-600 bg-red-50"
              label="Pricier here" value={fmtNum(summary.wePricier)} valueColor="text-red-600" />
            <StatCard icon={Store} color="text-gray-600 bg-gray-100"
              label="Most-often cheapest rival"
              value={topCheapestSeller ? topCheapestSeller[0] : '—'}
              sub={topCheapestSeller ? `cheapest on ${topCheapestSeller[1]} products` : ''} />
            <StatCard icon={Ruler} color="text-amber-600 bg-amber-50"
              label="Our size availability"
              value={ourSizeSummary.pct == null ? '—' : `${ourSizeSummary.pct}%`}
              valueColor={ourSizeSummary.pct == null ? 'text-gray-900'
                : ourSizeSummary.pct >= 60 ? 'text-emerald-600'
                : ourSizeSummary.pct >= 40 ? 'text-amber-600' : 'text-red-600'}
              sub={ourSizeSummary.withData
                ? `${fmtNum(ourSizeSummary.avail)}/${fmtNum(ourSizeSummary.total)} sizes · ${fmtNum(ourSizeSummary.fullyOut)} fully out`
                : 'no size data'} />
          </div>

          {overlap && (
            <div className="mt-6">
              <div className="flex flex-wrap items-end justify-between gap-2 mb-3">
                <div>
                  <div className="text-sm font-semibold text-gray-800">Product overlap</div>
                  <div className="text-xs text-gray-400">
                    {fmtNum(overlap.total)} {OUR_SITE} products. &quot;Only them&quot; is limited to our brands and
                    product groups (see the Not in our catalog tab).
                  </div>
                </div>
                <a
                  href={overlapXlsxUrl}
                  className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-xl border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <FileDown className="w-4 h-4" /> Download Excel
                </a>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {overlap.cards.map((c) => (
                  <div key={c.site} className={`rounded-xl border p-4 ${c.all ? 'border-gray-900 bg-gray-50' : 'border-gray-100 bg-white'}`}>
                    <div className="text-sm font-semibold text-gray-900">{c.all ? 'All competitors' : `${OUR_SITE} vs ${c.site}`}</div>
                    <div className="mt-3 grid grid-cols-3 gap-2">
                      <div>
                        <div className="text-[11px] text-gray-500">Both</div>
                        <div className="text-xl font-bold tabular-nums text-gray-900">{fmtNum(c.shared)}</div>
                      </div>
                      <div>
                        <div className="text-[11px] text-gray-500">Only us</div>
                        <div className="text-xl font-bold tabular-nums text-indigo-600">{fmtNum(c.onlyUs)}</div>
                      </div>
                      <div>
                        <div className="text-[11px] text-gray-500">Only them</div>
                        <div className="text-xl font-bold tabular-nums text-red-600">{fmtNum(c.onlyThem)}</div>
                        <div className="text-[11px] text-gray-400 tabular-nums">{fmtNum(c.onlyThemStock)} in stock</div>
                      </div>
                    </div>
                    {/* share bar: both / only us / only them */}
                    <div className="mt-3 flex h-1.5 rounded-full overflow-hidden bg-gray-100">
                      {[[c.shared, 'bg-gray-800'], [c.onlyUs, 'bg-indigo-500'], [c.onlyThem, 'bg-red-500']].map(([v, cls], i) => (
                        <div key={i} className={cls} style={{ width: `${(v / (c.shared + c.onlyUs + c.onlyThem || 1)) * 100}%` }} />
                      ))}
                    </div>
                    {c.all && (
                      <div className="mt-2 text-[11px] text-gray-500">
                        Both = at least one competitor sells it · Only us = none of them do
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* which sites the matched products appear on (overlap) — the overlap cards above replace it */}
          {!overlap && (
          <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
            <span className="text-gray-500">Matched products by site:</span>
            {COMP_SITES.map((s) => (
              <span key={s} className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
                {s}: <b className="tabular-nums">{fmtNum(summary.siteCounts[s])}</b>
              </span>
            ))}
            <span className="text-gray-400 text-xs">(a product can appear on more than one site)</span>
          </div>
          )}
        </div>
      </Card>

      {data?.hasMissing && (
        <div className="inline-flex rounded-xl border border-gray-200 bg-white p-1 shadow-sm">
          {[{ k: 'price', label: 'Price comparison' }, { k: 'missing', label: 'Not in our catalog' }].map((t) => (
            <button
              key={t.k} type="button" onClick={() => setTab(t.k)}
              className={`px-4 h-9 rounded-lg text-sm font-medium ${tab === t.k ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {tab === 'missing' && data?.hasMissing ? <CompetitorMissingProducts /> : (<>
      {/* Per-brand summary table */}
      <Card className="rounded-2xl overflow-hidden shadow-sm border border-gray-100">
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
          <span className="text-sm font-semibold text-gray-700">
            Summary by Brand <span className="font-normal text-gray-400">(sorted by matches · click a brand to filter)</span>
          </span>
        </div>
        <div className="overflow-auto max-h-80">
          <table className="min-w-full text-sm">
            <thead className="bg-white sticky top-0 z-10">
              <tr className="text-gray-500 border-b border-gray-100">
                <th className="text-left px-5 py-2.5 font-medium">Brand</th>
                <th className="text-right px-4 py-2.5 font-medium">Products</th>
                <th className="text-right px-4 py-2.5 font-medium">Matched</th>
                {COMP_SITES.map((s) => (
                  <th key={s} className="text-right px-4 py-2.5 font-medium" title={`Matched products found on ${s}`}>{s}</th>
                ))}
                <th className="text-right px-4 py-2.5 font-medium text-emerald-700">Cheaper here</th>
                <th className="text-right px-4 py-2.5 font-medium text-red-700">Pricier here</th>
                <th className="text-right px-5 py-2.5 font-medium">Cheapest seller</th>
              </tr>
            </thead>
            <tbody>
              {brandRows.map((b) => {
                const seller = Object.entries(b.cheapestBy || {})
                  .sort((x, y) => y[1] - x[1])
                  .filter(([, n]) => n > 0)[0];
                return (
                  <tr
                    key={b.brand}
                    onClick={() => setBrand(brand === b.brand ? '' : b.brand)}
                    className={`border-b border-gray-50 cursor-pointer hover:bg-indigo-50/60 ${
                      brand === b.brand ? 'bg-indigo-50' : ''
                    }`}
                  >
                    <td className="px-5 py-2 font-medium text-gray-900 capitalize">{b.brand}</td>
                    <td className="px-4 py-2 text-right text-gray-500 tabular-nums">{b.total}</td>
                    <td className="px-4 py-2 text-right font-semibold tabular-nums">{b.matched}</td>
                    {COMP_SITES.map((s) => (
                      <td key={s} className="px-4 py-2 text-right text-gray-600 tabular-nums">{b.siteCounts?.[s] || 0}</td>
                    ))}
                    <td className="px-4 py-2 text-right text-emerald-700 tabular-nums">{b.weCheapest}</td>
                    <td className="px-4 py-2 text-right text-red-700 tabular-nums">{b.wePricier}</td>
                    <td className="px-5 py-2 text-right text-gray-600">
                      {seller ? `${seller[0]} (${seller[1]})` : '—'}
                    </td>
                  </tr>
                );
              })}
              {brandRows.length === 0 && (
                <tr><td colSpan={5 + COMP_SITES.length} className="px-5 py-6 text-center text-gray-400">No matched brands.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Per-seller summary table */}
      <Card className="rounded-2xl overflow-hidden shadow-sm border border-gray-100">
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
          <span className="text-sm font-semibold text-gray-700">
            Summary by Seller{' '}
            <span className="font-normal text-gray-400">
              (sorted by matched products · click a seller to filter the table below
              {brand ? ` · ${brand} only` : ''})
            </span>
          </span>
        </div>
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-white">
              <tr className="text-gray-500 border-b border-gray-100">
                <th className="text-left px-5 py-2 font-medium">Seller</th>
                <th className="text-right px-4 py-2 font-medium">Matched</th>
                <th className="text-right px-4 py-2 font-medium text-emerald-600">We are cheaper</th>
                <th className="text-right px-4 py-2 font-medium text-red-500">We are pricier</th>
                <th className="text-right px-4 py-2 font-medium">Same</th>
                <th className="text-right px-4 py-2 font-medium" title="Mean of (our price − their price) ÷ their price. Positive = we are more expensive.">
                  Avg gap
                </th>
                <th className="text-right px-4 py-2 font-medium" title="Products where this seller has the lowest price in the market">
                  Market-cheapest
                </th>
                <th className="text-left px-4 py-2 font-medium" title="How the matches were established">Match quality</th>
                <th className="text-right px-5 py-2 font-medium">Data</th>
              </tr>
            </thead>
            <tbody>
              {sellerRows.map((s) => (
                <tr
                  key={s.site}
                  onClick={() => setSeller(seller === s.site ? '' : s.site)}
                  className={`border-b border-gray-50 cursor-pointer ${
                    seller === s.site ? 'bg-gray-100' : 'hover:bg-gray-50/60'}`}
                >
                  <td className="px-5 py-2 font-medium text-gray-800">{SITE_LABEL[s.site] || s.site}</td>
                  <td className="text-right px-4 py-2 tabular-nums font-semibold">{fmtNum(s.matched)}</td>
                  <td className="text-right px-4 py-2 tabular-nums text-emerald-600">{fmtNum(s.weCheaper)}</td>
                  <td className="text-right px-4 py-2 tabular-nums text-red-500">{fmtNum(s.wePricier)}</td>
                  <td className="text-right px-4 py-2 tabular-nums text-gray-400">{fmtNum(s.same)}</td>
                  <td className={`text-right px-4 py-2 tabular-nums ${
                    s.avgGap == null ? 'text-gray-300'
                      : s.avgGap > 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                    {s.avgGap == null ? '—' : `${s.avgGap > 0 ? '+' : ''}${s.avgGap.toFixed(1)}%`}
                  </td>
                  <td className="text-right px-4 py-2 tabular-nums text-gray-600">{fmtNum(s.cheapest)}</td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-1.5 text-[11px]">
                      {['gtin', 'code', 'name', 'model_gender'].map((m) => (
                        s.methods[m] ? (
                          <span key={m} className={MATCH_META[m]?.cls} title={MATCH_META[m]?.title}>
                            {MATCH_META[m]?.label} {s.methods[m]}
                          </span>
                        ) : null
                      ))}
                    </div>
                  </td>
                  <td className="text-right px-5 py-2 text-xs text-gray-400 tabular-nums">
                    {data?.dataDates?.[s.site] || '—'}
                  </td>
                </tr>
              ))}
              {sellerRows.length === 0 && (
                <tr><td colSpan={9} className="px-5 py-6 text-center text-gray-400">No sellers.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-2 text-xs text-gray-400 border-t border-gray-100">
          &quot;Avg gap&quot; compares our price to that seller only on the products both of us carry —
          a seller with few matches can show a large gap on a thin basis.
        </div>
      </Card>

      {/* Filters + detail table */}
      <Card className="rounded-2xl overflow-hidden shadow-sm border border-gray-100">
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex flex-wrap items-center gap-3">
          <select
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 h-9 text-sm bg-white"
          >
            <option value="">All brands</option>
            {(data?.brands || []).map((b) => (
              <option key={b.brand} value={b.brand}>
                {b.brand} ({b.matched}/{b.total})
              </option>
            ))}
          </select>
          <select
            value={seller}
            onChange={(e) => setSeller(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 h-9 text-sm bg-white"
            title="Compare against one seller instead of the whole market"
          >
            <option value="">All sellers (vs market min)</option>
            {sellerRows.map((s) => (
              <option key={s.site} value={s.site}>
                {SITE_LABEL[s.site] || s.site} ({s.matched})
              </option>
            ))}
          </select>
          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden bg-white">
            {[
              { k: '', label: 'All', n: positionCounts.all, cls: 'text-gray-700' },
              { k: 'pricier', label: 'We are pricier', n: positionCounts.pricier, cls: 'text-red-500' },
              { k: 'cheaper', label: 'We are cheaper', n: positionCounts.cheaper, cls: 'text-emerald-600' },
              { k: 'same', label: 'Same', n: positionCounts.same, cls: 'text-gray-500' },
            ].map((b, i) => (
              <button
                key={b.k || 'all'}
                type="button"
                onClick={() => setPosition(b.k)}
                className={`px-3 h-9 text-sm whitespace-nowrap ${i > 0 ? 'border-l border-gray-200' : ''} ${
                  position === b.k ? 'bg-gray-900 text-white' : `${b.cls} hover:bg-gray-50`
                }`}
              >
                {b.label} <span className="tabular-nums opacity-70">{fmtNum(b.n)}</span>
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search: product name, product ID…"
              className="border border-gray-300 rounded-lg pl-9 pr-3 h-9 text-sm w-72 bg-white"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={onlyMatched} onChange={(e) => setOnlyMatched(e.target.checked)} />
            Show only market-matched
          </label>
          {brand && (
            <button onClick={() => setBrand('')} className="text-sm text-indigo-600 underline">
              clear filter
            </button>
          )}
          <span className="text-sm text-gray-400 ml-auto tabular-nums">{fmtNum(rows.length)} rows</span>
        </div>
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-white sticky top-0 z-10 border-b border-gray-100">
              <tr>
                <th className="text-left px-5 py-3 font-semibold text-gray-700 sticky left-0 bg-white z-20">
                  Product
                </th>
                {ALL_SITES.map((s) => (
                  <th
                    key={s}
                    className={`text-right px-4 py-3 font-semibold ${
                      s === OUR_SITE ? 'text-indigo-700 bg-indigo-50' : 'text-gray-700'
                    }`}
                  >
                    {SITE_LABEL[s]}
                  </th>
                ))}
                <th className="text-right px-4 py-3 font-semibold text-gray-500">Market min</th>
                <th className="text-right px-5 py-3 font-semibold text-gray-500">Index</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="px-5 py-2 sticky left-0 bg-white z-10">
                    <div className="flex items-center gap-3">
                      {r.image ? (
                        <img src={r.image} alt="" className="w-10 h-10 object-cover rounded-lg border border-gray-200" loading="lazy" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-gray-100" />
                      )}
                      <div className="min-w-[220px]">
                        <div className="font-medium text-gray-900 leading-tight">
                          {r.urls?.[OUR_SITE] ? (
                            <a href={r.urls[OUR_SITE]} target="_blank" rel="noreferrer" className="hover:underline">
                              {r.name}
                            </a>
                          ) : r.name}
                        </div>
                        <div className="text-xs text-gray-400">
                          <span className="capitalize">{r.brand}</span>
                          {r.productId ? ` · ${r.productId}` : ''}{r.gender ? ` · ${r.gender}` : ''}
                        </div>
                      </div>
                    </div>
                  </td>
                  {ALL_SITES.map((s) => {
                    const v = r.prices?.[s];
                    const url = r.urls?.[s];
                    const isOurs = s === OUR_SITE;
                    const isCheapest = r.cheapestSite === s && r.matchCount > 0;
                    let cls = 'text-gray-700';
                    if (isOurs) {
                      cls =
                        r.position === 'cheaper' ? 'text-emerald-700 font-semibold bg-emerald-50'
                        : r.position === 'pricier' ? 'text-red-700 font-semibold bg-red-50'
                        : 'text-indigo-700 font-semibold bg-indigo-50';
                    } else if (isCheapest) {
                      cls = 'text-emerald-700 font-semibold';
                    }
                    const sz = r.sizes?.[s] || null;
                    const ex = r.excluded?.[s];
                    if (ex) {
                      return (
                        <td key={s} className="text-right px-4 py-2 tabular-nums text-gray-400">
                          <div className="leading-tight" title={ex.reason === 'sizes'
                            ? `Excluded from comparison: fewer than ${data?.minCompSizes || 2} sizes in stock`
                            : 'Excluded from comparison: out of stock at this seller'}>
                            {url ? (
                              <a href={url} target="_blank" rel="noreferrer" className="line-through hover:underline">{fmt(ex.price)}</a>
                            ) : (
                              <span className="line-through">{fmt(ex.price)}</span>
                            )}
                            {sz && sz.avail != null && <SizeBadge sz={sz} ours={false} />}
                            <div className="text-[10px] font-normal">{ex.reason === 'sizes' ? 'not counted' : 'out of stock'}</div>
                          </div>
                        </td>
                      );
                    }
                    return (
                      <td key={s} className={`text-right px-4 py-2 tabular-nums ${cls}`}>
                        <div className="leading-tight">
                          {v != null && url ? (
                            <a href={url} target="_blank" rel="noreferrer" className="hover:underline" title={r.methods?.[s] ? `match: ${r.methods[s]}` : ''}>
                              {fmt(v)}
                            </a>
                          ) : (
                            fmt(v)
                          )}
                          {sz && sz.avail != null && <SizeBadge sz={sz} ours={isOurs} />}
                          {!isOurs && v != null && r.methods?.[s] && <MatchBadge method={r.methods[s]} />}
                        </div>
                      </td>
                    );
                  })}
                  <td className="text-right px-4 py-2 text-gray-500 tabular-nums">{fmt(r.marketMin)}</td>
                  <td className={`text-right px-5 py-2 font-medium tabular-nums ${
                    r.priceIndex == null ? 'text-gray-300'
                    : r.priceIndex <= 100 ? 'text-emerald-600' : 'text-red-600'
                  }`}>
                    {r.priceIndex == null ? '—' : r.priceIndex}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && (
            <div className="p-8 text-center text-gray-400">No records found.</div>
          )}
        </div>
      </Card>
      </>)}

      {/* Off-screen full report — captured by html2canvas for the PDF only */}
      <div style={{ position: 'fixed', left: -10000, top: 0, pointerEvents: 'none' }} aria-hidden="true">
        <div
          ref={reportRef}
          style={{ width: 760, background: '#fff', padding: 28, color: '#111827', fontFamily: 'Inter, Arial, sans-serif', boxSizing: 'border-box' }}
        >
          {/* Title */}
          <div style={{ borderBottom: '2px solid #6366f1', paddingBottom: 10, marginBottom: 16 }}>
            <div style={{ fontSize: 22, fontWeight: 700 }}>Price Comparison — Summary by Brand</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
              {OUR_SITE}  ·  {brand ? `Brand: ${brand}` : `All brands (${brandRows.length})`}
              {'  ·  '}Competitors: {COMP_SITES.join(', ')}
            </div>
          </div>

          {/* KPI cards */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
            {[
              { label: 'Matched products', value: summary.matched, color: '#111827' },
              { label: 'Cheaper here', value: summary.weCheapest, color: POS_COLORS.cheaper },
              { label: 'Pricier here', value: summary.wePricier, color: POS_COLORS.pricier },
              { label: 'Equal / uncertain', value: Math.max(0, summary.matched - summary.weCheapest - summary.wePricier), color: '#6b7280' },
            ].map((k) => (
              <div key={k.label} style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: 12, padding: '12px 16px' }}>
                <div style={{ fontSize: 11, color: '#6b7280' }}>{k.label}</div>
                <div style={{ fontSize: 26, fontWeight: 700, color: k.color }}>{k.value}</div>
              </div>
            ))}
          </div>

          {/* Overall price position — donut + legend */}
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>Overall Price Position</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 24 }}>
            <PieChart width={240} height={200}>
              <Pie
                data={posData} dataKey="value" nameKey="name"
                cx="50%" cy="50%" innerRadius={48} outerRadius={88}
                isAnimationActive={false} stroke="#fff" strokeWidth={2}
              >
                {posData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Pie>
            </PieChart>
            <div style={{ flex: 1 }}>
              {posData.map((l) => (
                <div key={l.name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13 }}>
                  <span style={{ width: 12, height: 12, borderRadius: 3, background: l.color, display: 'inline-block' }} />
                  <span style={{ color: '#374151' }}>{l.name}</span>
                  <span style={{ marginLeft: 'auto', fontWeight: 600 }}>
                    {l.value} <span style={{ color: '#9ca3af', fontWeight: 400 }}>({posTotal ? ((l.value / posTotal) * 100).toFixed(1) : '0'}%)</span>
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Cheaper/pricier per brand — bar chart */}
          {barData.length > 0 && (
            <>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>By Brand: Cheaper vs Pricier Here</div>
              <BarChart
                width={704} height={260} data={barData}
                margin={{ top: 8, right: 8, left: 0, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="brand" angle={-25} textAnchor="end" interval={0} height={56} tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Legend />
                <Bar dataKey="cheaper" name="Cheaper here" stackId="a" fill={POS_COLORS.cheaper} isAnimationActive={false} />
                <Bar dataKey="pricier" name="Pricier here" stackId="a" fill={POS_COLORS.pricier} isAnimationActive={false} />
              </BarChart>
            </>
          )}

          {/* Brand summary table */}
          <div style={{ fontSize: 15, fontWeight: 700, margin: '16px 0 8px' }}>Summary by Brand (sorted by matches)</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#4f46e5', color: '#fff' }}>
                <th style={{ textAlign: 'left', padding: '7px 8px' }}>Brand</th>
                <th style={{ textAlign: 'right', padding: '7px 8px' }}>Products</th>
                <th style={{ textAlign: 'right', padding: '7px 8px' }}>Matched</th>
                {COMP_SITES.map((s) => <th key={s} style={{ textAlign: 'right', padding: '7px 8px' }}>{s}</th>)}
                <th style={{ textAlign: 'right', padding: '7px 8px' }}>Cheaper here</th>
                <th style={{ textAlign: 'right', padding: '7px 8px' }}>Pricier here</th>
                <th style={{ textAlign: 'right', padding: '7px 8px' }}>Cheapest seller</th>
              </tr>
            </thead>
            <tbody>
              {brandRows.map((b, i) => {
                const seller = Object.entries(b.cheapestBy || {})
                  .sort((x, z) => z[1] - x[1]).filter(([, n]) => n > 0)[0];
                return (
                  <tr key={b.brand} style={{ background: i % 2 ? '#f8fafc' : '#fff', borderBottom: '1px solid #eef2f7' }}>
                    <td style={{ padding: '6px 8px', fontWeight: 600, textTransform: 'capitalize' }}>{b.brand}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: '#6b7280' }}>{b.total}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>{b.matched}</td>
                    {COMP_SITES.map((s) => <td key={s} style={{ padding: '6px 8px', textAlign: 'right', color: '#4b5563' }}>{b.siteCounts?.[s] || 0}</td>)}
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: POS_COLORS.cheaper, fontWeight: 600 }}>{b.weCheapest}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: POS_COLORS.pricier, fontWeight: 600 }}>{b.wePricier}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: '#4b5563' }}>{seller ? `${seller[0]} (${seller[1]})` : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Match confidence: barcode / model+color = exact, model = model-level, different color = approximate
// Keys must mirror match_method written by scripts/match_high5_competitors.sql
// ('gtin' | 'name' | 'model_gender'). The name_* keys below are legacy values kept
// so older snapshots still render a badge.
const MATCH_META = {
  gtin:         { label: 'barcode',      cls: 'text-emerald-600', title: 'Exact match by barcode (GTIN) — strongest' },
  code:         { label: 'style code',   cls: 'text-emerald-600', title: 'Exact match by manufacturer style + colour code' },
  name:         { label: 'model+color',  cls: 'text-emerald-600', title: 'Brand + model + color match' },
  model_gender: { label: 'model+gender', cls: 'text-amber-600',   title: 'Brand + model + gender match — colour NOT confirmed. Safe for price (same model+gender is near-uniform in price) but this is a model-level, not colour-level, match.' },
  name_color:   { label: 'model+color',  cls: 'text-emerald-600', title: 'Brand + model + color match' },
  name_model:   { label: 'model',        cls: 'text-amber-600',   title: 'Brand + model match (color not confirmed)' },
  name_xcolor:  { label: 'model ~color', cls: 'text-gray-400',    title: 'Same model, different color variant (approximate)' },
};
function MatchBadge({ method }) {
  const m = MATCH_META[method];
  if (!m) return null;
  return <div className={`text-[10px] font-normal ${m.cls}`} title={m.title}>{m.label}</div>;
}

// Number of unique in-stock sizes (size breakage): avail/total, under the price.
// `sz.list` (our own column only) carries the actual size ladder for the tooltip.
function SizeBadge({ sz, ours }) {
  const ratio = sz.total ? sz.avail / sz.total : 0;
  const cls = sz.avail === 0 ? 'text-red-500' : ratio <= 0.34 ? 'text-amber-600' : 'text-emerald-600';
  let title = 'in-stock / listed sizes';
  if (Array.isArray(sz.list) && sz.list.length) {
    const inS = sz.list.filter((x) => x.inStock).map((x) => x.size);
    const outS = sz.list.filter((x) => !x.inStock).map((x) => x.size);
    title = `In stock: ${inS.length ? inS.join(', ') : 'none'}\nOut of stock: ${outS.length ? outS.join(', ') : 'none'}`;
  }
  return (
    <div className={`text-[11px] font-normal ${cls} ${ours ? 'cursor-help underline decoration-dotted underline-offset-2' : ''}`} title={title}>
      {sz.avail}{sz.total ? `/${sz.total}` : ''} sizes
    </div>
  );
}

function StatCard({ icon: Icon, color, label, value, sub, valueColor = 'text-gray-900' }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-3 flex items-center gap-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-xs text-gray-500 truncate">{label}</div>
        <div className={`text-lg font-bold tabular-nums truncate ${valueColor}`}>{value}</div>
        {sub && <div className="text-[11px] text-gray-400 truncate">{sub}</div>}
      </div>
    </div>
  );
}
